import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Style from "../../style/eventfilter.module.scss";
import EventFilterLeft from "./EventFilterLeft";
import EventFilterMiddle from "./EventFilterMiddle";
import EventFilterRight from "./EventFilterRight";
import StickyBox from "react-sticky-box";

const EventFilter = () => {
  const { type, city, category, designation } = useParams();
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);

  const formatText = (slug: string | undefined) =>
    slug
      ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "";

  const toSlug = (name: string) =>
    name.toLowerCase().replace(/ & /g, "-&-").replace(/\s+/g, "-");

  useEffect(() => {
    fetch("/data/event.json")
      .then((res) => res.json())
      .then((res) => {
        const events = res.events;
        setData(events);
        const categories: string[] = Array.from(
          new Set(events.flatMap((item: any) => item.categories))
        );
        setAllCategories(categories);
      });
  }, []);

  useEffect(() => {
    const today = new Date();
    const formattedCategory = category?.toLowerCase().replace(/-/g, " ");
    const formattedType = type?.toLowerCase().replace(/-/g, " ");
    const formattedCity = city?.toLowerCase().replace(/-/g, " ");
    const formattedDesignation = designation?.toLowerCase().replace(/-/g, " ");

    const filteredToday = data.filter((item) => {
      const start = new Date(item.start_date);
      const isToday = start.toDateString() === today.toDateString();

      const matchesCategory = formattedCategory
        ? item.categories.some(
            (cat: string) => toSlug(cat) === toSlug(formattedCategory)
          )
        : true;

      const matchesType = formattedType
        ? item.tags?.some(
            (tag: string) => toSlug(tag) === toSlug(formattedType)
          )
        : true;

      const matchesCity = formattedCity
        ? toSlug(item.venue.city) === toSlug(formattedCity)
        : true;

      const matchesDesignation = formattedDesignation
        ? toSlug(item.organiser?.contact?.designation || "") ===
          toSlug(formattedDesignation)
        : true;

      return (
        isToday &&
        matchesCategory &&
        matchesType &&
        matchesDesignation &&
        matchesCity
      );
    });

    if (filteredToday.length > 0) {
      setFilteredData(filteredToday);
    } else {
      const upcoming = data.filter((item) => {
        const matchesCategory = formattedCategory
          ? item.categories.some(
              (cat: string) => toSlug(cat) === toSlug(formattedCategory)
            )
          : true;

        const matchesType = formattedType
          ? item.tags?.some(
              (tag: string) => toSlug(tag) === toSlug(formattedType)
            )
          : true;

        const matchesCity = formattedCity
          ? toSlug(item.venue.city) === toSlug(formattedCity)
          : true;

        const matchesDesignation = formattedDesignation
          ? toSlug(item.organiser?.contact?.designation || "") ===
            toSlug(formattedDesignation)
          : true;

        return (
          matchesCategory && matchesType && matchesDesignation && matchesCity
        );
      });

      setFilteredData(upcoming);
    }
  }, [type, category, city, designation, data]);

  const handleDateFilter = (filterType: string) => {
    const today = new Date();
    const compare = (dateStr: string) => new Date(dateStr);

    const result = data.filter((item: any) => {
      const start = compare(item.start_date);

      switch (filterType) {
        case "today":
          return start.toDateString() === today.toDateString();
        case "tomorrow":
          const tomorrow = new Date(today);
          tomorrow.setDate(today.getDate() + 1);
          return start.toDateString() === tomorrow.toDateString();
        case "this-week":
          const startOfWeek = new Date(today);
          const endOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          return start >= startOfWeek && start <= endOfWeek;
        case "this-month":
          return (
            start.getMonth() === today.getMonth() &&
            start.getFullYear() === today.getFullYear()
          );
        case "next-3-months":
          const threeMonthsLater = new Date(today);
          threeMonthsLater.setMonth(today.getMonth() + 3);
          return start >= today && start <= threeMonthsLater;
        default:
          return true;
      }
    });

    setFilteredData(result);
  };

  const pageTitle = formatText(type || category || designation || city);
  const visibleCategories = allCategories
    .filter((cat) => toSlug(cat) !== toSlug(category || type || ""))
    .slice(0, 4);

  return (
    <div className={Style.container}>
      <div className={Style.wrapper}>
        <h1 className={Style.pageTitle}>{pageTitle} Events</h1>

        <div className={Style.Category}>
          {city &&
            visibleCategories.map((cat) => (
              <a
                key={cat}
                href={`/event/${toSlug(cat)}/${toSlug(city)}`}
                className={Style.categoryItem}
              >
                {cat}
              </a>
            ))}
        </div>

        <div className={Style.Divide}>
          <div className={Style.leftSide}>
            <StickyBox offsetTop={20} offsetBottom={20}>
              <EventFilterLeft
                itemList={data}
                onDateFilter={handleDateFilter}
              />
            </StickyBox>
          </div>
          <div className={Style.middle}>
            <EventFilterMiddle itemList={filteredData} />
          </div>
          <div className={Style.rightSide}>
            <EventFilterRight itemList={data} filteredData={filteredData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventFilter;
